import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ChevronRight, ListChecks, X } from "lucide-react";
import { api } from "@/lib/api";
import { useDownloadFolderName } from "@/lib/downloadFolder";

const OPEN_KEY = "clubfin:stepplan:open";
const readOpen = () => { try { return localStorage.getItem(OPEN_KEY) !== "0" && window.innerWidth >= 768; } catch { return true; } };
const writeOpen = (v) => { try { localStorage.setItem(OPEN_KEY, v ? "1" : "0"); } catch { /* ignore */ } };
const noLoansKey = (clubId) => `clubfin:no-existing-loans:${clubId}`;

function useCookieNoticeVisible() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const check = () => { try { setVisible(!localStorage.getItem("mjp_cookie_notice_v1")); } catch { setVisible(false); } };
    check();
    const id = setInterval(check, 1500);
    return () => clearInterval(id);
  }, []);
  return visible;
}

// Haalt de clubdata voor het stappenplan op als de pagina ze niet zelf al heeft.
function useClubStepData(clubId, provided) {
  const [fetched, setFetched] = useState(null);
  useEffect(() => {
    if (!clubId || provided) return undefined;
    let alive = true;
    Promise.all([...["historicals", "depreciations", "existing-loans", "scenarios"].map((p) => api.get(`/clubs/${clubId}/${p}`).then((r) => r.data).catch(() => [])),
      api.get(`/clubs/${clubId}`).then((r) => r.data).catch(() => null)])
      .then(([historicals, depreciations, existingLoans, scenarios, club]) => { if (alive) setFetched({ historicals, depreciations, existingLoans, scenarios, club }); });
    return () => { alive = false; };
  }, [clubId, provided]);
  return provided || fetched;
}

const openFolderPicker = () => {
  window.scrollTo({ top: 0, behavior: "smooth" });
  setTimeout(() => document.querySelector('[data-testid="download-folder-btn"]')?.click(), 350);
};

// Zwevend stappenplan (linksonder): icoon → balk met 6 stappen; "Verberg" brengt het icoon terug.
// Klapt automatisch in zodra alle stappen klaar zijn. Stand wordt onthouden in localStorage.
export default function StepPlan({ clubId, data, clubs }) {
  const nav = useNavigate();
  const cookieVisible = useCookieNoticeVisible();
  const clubData = useClubStepData(clubId, data);
  const clubFolder = useDownloadFolderName(clubId);
  const globalFolder = useDownloadFolderName(null);
  const [open, setOpen] = useState(readOpen);
  const [ackTick, setAckTick] = useState(0);
  const reopened = useRef(false);

  const steps = useMemo(() => {
    const list = [];
    if (clubs) list.push({ key: "club", label: "Maak je club aan", done: clubs.length > 0, action: () => document.querySelector('[data-testid="new-club-btn"]')?.click() });
    if (!clubId) return list;
    const d = clubData || {};
    const club = d.club || {};
    // BTW geldt als vastgelegd zodra de BTW-tab één keer is opgeslagen/bevestigd, of het regime al plichtig/gemengd is.
    const btwDone = Boolean(club.btw_confirmed) || ["plichtig", "gemengd"].includes(club.btw_regime);
    const noLoansAck = (() => { try { return localStorage.getItem(noLoansKey(clubId)) === "1"; } catch { return false; } })();
    void ackTick;
    const go = (tab) => nav(`/clubs/${clubId}${tab ? `?tab=${tab}` : ""}`);
    list.push(
      { key: "folder", label: "Definieer de down-/up-loadmap op je eigen PC", done: Boolean(clubFolder || globalFolder), action: openFolderPicker },
      { key: "btw", label: "Leg de BTW-situatie van de club vast", hint: "Vrijgesteld, BTW-plichtig of gemengd — bepaalt of je incl. of excl. BTW invult", done: btwDone, action: () => go("btw") },
      { key: "hist", label: "Voer cijfers voorgaande jaren in", hint: "AI-import of Excel", done: (d.historicals || []).length > 0, action: () => go("historicals") },
      { key: "dep", label: "Voer de afschrijvingsstaat in", done: (d.depreciations || []).length > 0, action: () => go("depreciations") },
      { key: "loans", label: "Voer bestaande financiering in", optional: true, done: (d.existingLoans || []).length > 0 || noLoansAck, action: () => go("existing-loans"),
        secondary: { label: "Geen leningen", onClick: () => { try { localStorage.setItem(noLoansKey(clubId), "1"); } catch { /* ignore */ } setAckTick((n) => n + 1); } } },
      { key: "scen", label: "Maak je eerste scenario", done: (d.scenarios || []).length > 0, action: () => nav(`/clubs/${clubId}/scenarios/new`) },
    );
    return list;
  }, [clubs, clubId, clubData, clubFolder, globalFolder, ackTick, nav]);

  const doneCount = steps.filter((s) => s.done).length;
  const loaded = Boolean(clubs) || Boolean(clubData);
  const allDone = loaded && steps.length > 0 && doneCount === steps.length;
  // De downloadmap is per browser; zodra de inhoudelijke stappen klaar zijn klapt het plan vanzelf in.
  const coreDone = loaded && steps.length > 0 && steps.filter((s) => s.key !== "folder").every((s) => s.done);

  useEffect(() => { if (coreDone && !reopened.current) setOpen(false); }, [coreDone]);

  if (!clubId && !clubs) return null;
  const bottom = cookieVisible ? "bottom-20 md:bottom-4" : "bottom-4";
  const show = () => { reopened.current = true; setOpen(true); writeOpen(true); };
  const hide = () => { setOpen(false); writeOpen(false); };

  if (!open) {
    return (
      <button type="button" onClick={show} data-testid="stepplan-toggle" aria-label="Stappenplan openen"
        className={`fixed left-4 ${bottom} z-40 btn-brutal bg-card text-foreground h-12 pl-3 pr-4 inline-flex items-center gap-2 font-display uppercase font-black text-xs tracking-widest print:hidden`}>
        <span className={`w-7 h-7 inline-flex items-center justify-center border-2 border-foreground ${allDone ? "bg-positive text-background" : "bg-primary text-primary-foreground"}`}>
          {allDone ? <Check className="w-4 h-4" /> : <ListChecks className="w-4 h-4" />}
        </span>
        Stappenplan
        {loaded && <span className="font-mono-num text-[10px] text-muted-foreground" data-testid="stepplan-progress">{doneCount}/{steps.length}</span>}
      </button>
    );
  }

  return (
    <aside data-testid="stepplan" className={`fixed left-4 ${bottom} z-40 w-[calc(100vw-6.5rem)] md:w-[360px] bg-card border-2 border-foreground heavy-shadow print:hidden`}>
      <header className="flex items-center justify-between gap-2 px-3 py-2 border-b-2 border-foreground bg-foreground text-background">
        <div className="inline-flex items-center gap-2 min-w-0">
          <ListChecks className="w-4 h-4 shrink-0" />
          <span className="font-display uppercase font-black text-sm tracking-wide">Stappenplan</span>
          {loaded && <span className="font-mono-num text-[11px] opacity-80" data-testid="stepplan-progress">{doneCount} van {steps.length} klaar</span>}
        </div>
        <button type="button" onClick={hide} data-testid="stepplan-hide" className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-display uppercase font-bold tracking-widest hover:bg-background/20">
          Verberg <X className="w-3.5 h-3.5" />
        </button>
      </header>
      <ol className="divide-y divide-border">
        {steps.map((s, i) => (
          <li key={s.key} data-testid={`stepplan-step-${s.key}`} className={`flex items-center gap-3 px-3 py-2 ${s.done ? "bg-primary/5" : ""}`}>
            <span className={`shrink-0 w-7 h-7 inline-flex items-center justify-center border-2 font-display font-black text-sm ${s.done ? "border-primary bg-primary text-primary-foreground" : "border-foreground bg-background"}`}>
              {s.done ? <Check className="w-4 h-4" /> : i + 1}
            </span>
            <button type="button" onClick={s.action} data-testid={`stepplan-action-${s.key}`} className="flex-1 min-w-0 text-left group">
              <span className={`block text-xs font-display uppercase font-bold tracking-wide leading-snug ${s.done ? "text-muted-foreground line-through decoration-primary/60" : "group-hover:text-primary"}`}>
                {s.label}
                {s.optional && <span className="ml-1.5 px-1 py-px border border-muted-foreground/60 text-muted-foreground text-[9px] tracking-widest no-underline">optioneel</span>}
              </span>
              {!s.done && s.hint && <span className="block text-[10px] text-muted-foreground">{s.hint}</span>}
            </button>
            {!s.done && s.secondary && (
              <button type="button" onClick={s.secondary.onClick} data-testid={`stepplan-secondary-${s.key}`}
                className="shrink-0 text-[10px] font-display uppercase font-bold tracking-widest text-primary hover:underline">{s.secondary.label}</button>
            )}
            {!s.done && <ChevronRight className="w-4 h-4 shrink-0 text-muted-foreground" />}
          </li>
        ))}
      </ol>
      {clubs && clubs.length === 0 && <p className="px-3 py-2 text-[11px] text-muted-foreground border-t border-border">Na het aanmaken van je club verschijnen hier de volgende stappen.</p>}
      {allDone && <p className="px-3 py-2 text-[11px] text-positive font-bold border-t border-border" data-testid="stepplan-done">Alles staat klaar — veel succes met je meerjarenplan!</p>}
    </aside>
  );
}
